/**
 * AsyncMultiCombobox — the multi-select sibling of ``AsyncCombobox`` (ME3a).
 *
 * Same server-paged search (shared via ``useAsyncPagedSearch``), same
 * token-only theming, same domain-blindness: it knows nothing about
 * tenants or units, only about ids and labels a caller supplies.
 *
 * What differs from the single-select, and why:
 *
 * * **Selection is a list.** ``value`` is an array of ids, ``onChange``
 *   receives the next array. Picking an already-selected row REMOVES it
 *   (a toggle), which is what the checkmark in the row promises.
 * * **The popover stays open** after a pick. Choosing three tenants
 *   should cost one open, not three.
 * * **Selected rows are chips** under the trigger, each removable on its
 *   own, and each remove button names its label rather than saying
 *   "remove" — a screen reader hearing seven identical buttons learns
 *   nothing.
 * * **``selectedItems`` carries the labels.** An edit form must show the
 *   names of links that are nowhere near page 1 of the search, and it
 *   must not spend one request per selected id to learn them. ME2a's
 *   read arrays already ship ``{id, label}``, so the caller hands them
 *   straight in.
 *
 * A selected id whose label is unknown renders as a neutral placeholder,
 * never as the raw id — a UUID on screen is exactly what this component
 * exists to avoid.
 */
import * as React from "react";
import { Check, ChevronsUpDown, Loader2, X } from "lucide-react";

import useAsyncPagedSearch, {
  DEFAULT_PAGE_SIZE,
} from "./useAsyncPagedSearch";

import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import {
  Command,
  CommandInput,
  CommandList,
  CommandItem,
} from "@/components/ui/command";

/** Shown when an id is selected but nothing can name it yet. */
export const UNKNOWN_LABEL = "Wird geladen …";

export default function AsyncMultiCombobox({
  label,
  placeholder = "— keine —",
  searchPlaceholder = "Suchen …",
  loadingText = "Lädt …",
  emptyText = "Keine Treffer.",
  errorText = "Suche konnte nicht geladen werden.",
  /** string[] — the selected ids, in the order the user picked them. */
  value = [],
  /** Array<{id, label}> — labels for ids the search has not loaded. */
  selectedItems = [],
  /** (nextIds: string[]) => void */
  onChange,
  fetchPage,
  getItemId,
  getItemLabel,
  renderItem,
  pageSize = DEFAULT_PAGE_SIZE,
  disabled = false,
  /** Optional id => row, to name a selected id no array covered. */
  hydrateById = null,
  /** Rendered under the chips — the derived-links hint (ME3a Phase 6). */
  footer = null,
}) {
  const [open, setOpen] = React.useState(false);
  const {
    query,
    setQuery,
    items,
    total,
    loading,
    loadingMore,
    error,
    hasMore,
    reload,
    loadMore,
  } = useAsyncPagedSearch({
    open,
    fetchPage,
    getItemId,
    pageSize,
    errorText,
  });

  const triggerId = React.useId();
  const [listboxId, setListboxId] = React.useState(null);
  const handleListRef = React.useCallback((node) => {
    setListboxId(node?.id || null);
  }, []);

  const ids = React.useMemo(
    () => (Array.isArray(value) ? value.map(String) : []),
    [value]
  );
  const idSet = React.useMemo(() => new Set(ids), [ids]);

  // Label lookup: whatever the caller hydrated, plus anything the search
  // has since loaded. The search half matters after a fresh pick — the
  // caller has not re-rendered a new `selectedItems` yet.
  const labelById = React.useMemo(() => {
    const map = new Map();
    (selectedItems || []).forEach((it) => {
      if (!it) return;
      const id = String(it.id ?? getItemId?.(it) ?? "");
      const text = (it.label || "").trim();
      if (id && text) map.set(id, text);
    });
    (items || []).forEach((it) => {
      const id = String(getItemId(it));
      const text = (getItemLabel(it) || "").trim();
      if (id && text && !map.has(id)) map.set(id, text);
    });
    return map;
  }, [selectedItems, items, getItemId, getItemLabel]);

  // Name ids that nothing else could name. This does NOT fire for a link
  // that came from a read array — those ship their own label — only for a
  // create-mode prefill, where there is no array to read. One request per
  // unnamed id, once, and never again while it stays selected.
  const [fetched, setFetched] = React.useState({});
  const askedRef = React.useRef(new Set());
  // Alive for the component's LIFETIME, not per effect run. A per-run
  // flag looked right and was wrong: `labelById` and `getItemLabel` are
  // fresh identities on every render, so the effect re-runs constantly
  // and its cleanup cancelled the request still in flight — the chip
  // never got its name.
  const aliveRef = React.useRef(true);
  React.useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const labelOf = React.useRef(getItemLabel);
  labelOf.current = getItemLabel;

  React.useEffect(() => {
    if (!hydrateById) return;
    ids
      .filter((id) => !labelById.has(id) && !askedRef.current.has(id))
      .forEach((id) => {
        askedRef.current.add(id);
        Promise.resolve(hydrateById(id))
          .then((row) => {
            if (!aliveRef.current || !row) return;
            const text = (labelOf.current(row) || "").trim();
            if (text) setFetched((prev) => ({ ...prev, [id]: text }));
          })
          .catch(() => {
            // A deleted or invisible row must not break the form; the
            // chip keeps its placeholder and the link stays intact.
          });
      });
  }, [ids, labelById, hydrateById]);

  const nameFor = (id) => labelById.get(id) || fetched[id] || null;

  const toggle = (id) => {
    const key = String(id);
    onChange(
      idSet.has(key) ? ids.filter((x) => x !== key) : [...ids, key]
    );
    // Popover deliberately stays open — picking several is the point.
  };

  const remove = (id) => onChange(ids.filter((x) => x !== String(id)));

  return (
    <div>
      <label
        htmlFor={triggerId}
        className="mb-1 block text-[11px] font-medium text-muted-foreground"
      >
        {label}
      </label>

      <Popover open={open} onOpenChange={disabled ? undefined : setOpen}>
        <PopoverTrigger asChild>
          <button
            id={triggerId}
            type="button"
            role="combobox"
            aria-expanded={open}
            aria-controls={open && listboxId ? listboxId : undefined}
            disabled={disabled}
            className="flex w-full items-center justify-between rounded-[8px] border border-input bg-background px-3 py-2 text-[13px] text-foreground outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-50"
          >
            <span
              className={cn(
                "truncate text-left",
                ids.length === 0 && "text-muted-foreground"
              )}
            >
              {ids.length === 0
                ? placeholder
                : `${ids.length} ausgewählt`}
            </span>
            <ChevronsUpDown
              className="ml-2 h-4 w-4 shrink-0 opacity-50"
              aria-hidden="true"
            />
          </button>
        </PopoverTrigger>

        <PopoverContent
          align="start"
          className="z-[60] p-0"
          style={{ width: "var(--radix-popover-trigger-width)" }}
        >
          <Command shouldFilter={false} className="bg-transparent">
            <CommandInput
              placeholder={searchPlaceholder}
              value={query}
              onValueChange={setQuery}
            />
            <CommandList ref={handleListRef}>
              <div aria-live="polite" className="sr-only">
                {loading
                  ? loadingText
                  : error
                    ? errorText
                    : `${items.length} von ${total} Ergebnissen, ${ids.length} ausgewählt`}
              </div>

              {error ? (
                <div className="px-3 py-4 text-[12px]" role="alert">
                  <p className="m-0 text-destructive">{errorText}</p>
                  <button
                    type="button"
                    onClick={reload}
                    className="mt-2 rounded-[6px] border border-border px-2 py-1 text-[11px] font-semibold text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    Erneut versuchen
                  </button>
                </div>
              ) : null}

              {!error && loading ? (
                <p className="flex items-center gap-2 px-3 py-4 text-[12px] text-muted-foreground">
                  <Loader2
                    className="h-3.5 w-3.5 animate-spin"
                    aria-hidden="true"
                  />
                  {loadingText}
                </p>
              ) : null}

              {!error && !loading && items.length === 0 ? (
                <p className="px-3 py-4 text-center text-[12px] text-muted-foreground">
                  {emptyText}
                </p>
              ) : null}

              {!error
                ? items.map((item) => {
                    const id = String(getItemId(item));
                    const picked = idSet.has(id);
                    return (
                      <CommandItem
                        key={id}
                        value={id}
                        aria-selected={picked}
                        onSelect={() => toggle(item.id ?? id)}
                        className="items-start text-foreground data-[selected=true]:bg-muted data-[selected=true]:text-foreground"
                      >
                        <Check
                          className={cn(
                            "mr-2 mt-0.5 h-4 w-4 shrink-0",
                            picked ? "opacity-100" : "opacity-0"
                          )}
                          aria-hidden="true"
                        />
                        <span className="min-w-0 flex-1">
                          {renderItem(item)}
                        </span>
                      </CommandItem>
                    );
                  })
                : null}

              {!error && hasMore ? (
                <div className="px-2 py-2">
                  <button
                    type="button"
                    onClick={loadMore}
                    disabled={loadingMore}
                    className="w-full rounded-[6px] border border-border px-2 py-1.5 text-[11px] font-medium text-foreground hover:bg-muted disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    {loadingMore ? "Lädt …" : "Mehr laden"}
                  </button>
                </div>
              ) : null}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Chips: wrap rather than scroll sideways, so a narrow screen
          grows downwards instead of hiding selections off-screen. */}
      {ids.length > 0 ? (
        <ul className="mt-2 flex list-none flex-wrap gap-1.5 p-0">
          {ids.map((id) => {
            const text = nameFor(id) || UNKNOWN_LABEL;
            return (
              <li key={id}>
                <span className="inline-flex max-w-full items-center gap-1 rounded-[8px] border border-border bg-muted/60 py-1 pl-2.5 pr-1 text-[12px] font-medium text-foreground dark:border-[#1e2130] dark:bg-[#0d0f14] dark:text-[#c5cbe0]">
                  <span className="truncate" title={text}>
                    {text}
                  </span>
                  <button
                    type="button"
                    onClick={() => remove(id)}
                    disabled={disabled}
                    aria-label={`${text} entfernen`}
                    className="rounded-[6px] p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
                  >
                    <X className="h-3 w-3" aria-hidden="true" />
                  </button>
                </span>
              </li>
            );
          })}
        </ul>
      ) : null}

      {footer}
    </div>
  );
}
